/**
 * Page numbers as ranges, for display AND for storage.
 *
 * The storage half exists because `AppliedLexiconRecord.incomplete` is
 * persisted into the `document-redaction-data` asset on every autosave, and
 * the list it carries can be document-length: an index that dies early on the
 * 1,602-page fixture leaves ~1,600 pages unsearched, per applied lexicon —
 * ~8 KB of JSON per record, re-uploaded every five minutes and kept for five
 * generations. As ranges it is one pair.
 *
 * RANGING IS LOSSLESS UNCONDITIONALLY — `toPageRanges` is a bijection on sets
 * of page numbers, contiguous or not. What contiguity changes is the RATIO,
 * and it is worth being exact about that, because ranging is not always a win:
 *
 *   - `unindexedPages`, the index's tail, is contiguous and grows with the
 *     DOCUMENT. That is the case this field exists for, and the one that turns
 *     ~1,600 numbers into one pair.
 *   - `timedOutPages` / `crashedPages` scatter freely, and a fully scattered
 *     set encodes as N singleton spans — 2N numbers, WORSE than the flat array.
 *
 * THE BOUND IS THE COMPARISON, not an absolute. Ranging is never worse than
 * twice the flat array and never better than the flat array divided by the run
 * length, so the worst case is 2x a list that could have been written anyway,
 * and the case this exists for is ~1,600:1. Do not reach for a page-count
 * bound to justify it: an earlier version of this header claimed the scattered
 * sets were bounded by the apply budget at one deadline a page, which is false
 * for `crashedPages` — a crash settles `runOnePage` immediately through the
 * `error` event and consumes no deadline at all, so an intermittently crashing
 * worker can scatter them across a whole document for free.
 *
 * KEEP THIS MODULE DEPENDENCY-FREE. `models.ts` — which defines the persisted
 * contract — imports the codec half, so anything this file imports becomes a
 * dependency of the type layer. If `formatRanges` ever needs `intl` (locale
 * digit separators, RTL, en-dash vs hyphen), split the formatter out rather
 * than importing it here.
 */

/**
 * An inclusive `[first, last]` page span. Mutable (not `readonly`) on purpose:
 * it is written into `PersistedRedactionState`, whose contract is that
 * `JSON.parse(JSON.stringify(payload))` is structurally the payload.
 */
export type PageRange = [number, number];

/** `[2, 3, 4, 9]` → `[[2, 4], [9, 9]]`. Order-insensitive; de-duplicates. */
export function toPageRanges(pages: readonly number[]): PageRange[] {
  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  const ranges: PageRange[] = [];
  let i = 0;
  while (i < sorted.length) {
    const start = sorted[i]!;
    let end = start;
    while (sorted[i + 1] === end + 1) {
      end += 1;
      i += 1;
    }
    ranges.push([start, end]);
    i += 1;
  }
  return ranges;
}

/**
 * `[[2, 4], [9, 9]]` → "2–4, 9".
 *
 * No `fromPageRanges` inverse is exported. Nothing in the app expands a stored
 * range back into page numbers — the legacy migration goes the other way
 * (`toPageRanges`) and every consumer reads ranges directly — and an exported
 * expander is the one function here that turns a one-pair span into an
 * arbitrarily long array from untrusted asset data.
 */
export function formatRanges(ranges: readonly PageRange[]): string {
  return ranges
    .map(([start, end]) => (start === end ? `${start}` : `${start}–${end}`))
    .join(', ');
}

/** Page numbers as ranges: `[2, 3, 4, 9]` → "2–4, 9". Order-insensitive. */
export function formatPageRanges(pages: readonly number[]): string {
  return formatRanges(toPageRanges(pages));
}
